import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PhoneIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { clsx } from 'clsx';
import { Campaign, CampaignStatus } from '../../types/campaign';
import { campaignService } from '../../services/campaignService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { SkeletonCard } from '../../components/UI/SkeletonLoader';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import toast from 'react-hot-toast';

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const isUser = user?.role === 'user' || user?.role === 'agent';
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [campaignCounts, setCampaignCounts] = useState<{ [status: string]: number; total: number }>({
    all: 0,
    active: 0,
    paused: 0,
    draft: 0,
    completed: 0,
    cancelled: 0,
    trash: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | CampaignStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const campaignsPerPage = 12;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteProgressByCampaign, setDeleteProgressByCampaign] = useState<
    Record<
      string,
      {
        status: 'queued' | 'running' | 'completed' | 'failed';
        progressPercent: number;
        deletedLeads: number;
        totalLeads: number;
        errorMessage: string | null;
      }
    >
  >({});
  // Ref to avoid re-sweeping all trash campaigns on every campaigns state change
  const sweepDoneRef = useRef(false);
  // Ref to read latest deleteProgressByCampaign inside interval without stale closure
  const deleteProgressRef = useRef(deleteProgressByCampaign);

  const tabs = [
    { key: 'all' as const, label: 'All', count: campaignCounts.all || 0 },
    { key: 'active' as const, label: 'Active', count: campaignCounts.active || 0 },
    { key: 'paused' as const, label: 'Paused', count: campaignCounts.paused || 0 },
    { key: 'draft' as const, label: 'Draft', count: campaignCounts.draft || 0 },
    { key: 'completed' as const, label: 'Completed', count: campaignCounts.completed || 0 },
    { key: 'trash' as const, label: 'Trash', count: campaignCounts.trash || 0 },
  ];

  useEffect(() => {
    fetchCampaignCounts();
  }, [isAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        page: currentPage,
        limit: campaignsPerPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeTab === 'all'
          ? { excludeStatus: 'trash' as const }
          : { status: [activeTab] as CampaignStatus[] }),
      };

      const response = isAdmin
        ? await campaignService.getAllCampaignsForAdmin(filters)
        : await campaignService.getCampaigns(filters);

      setCampaigns(response.campaigns);
      setTotalCampaigns(response.total ?? response.campaigns.length);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, debouncedSearch, isAdmin, campaignsPerPage]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const trashCampaignIds = campaigns
    .filter(c => c.status === 'trash')
    .map(c => c.id)
    .sort()
    .join(',');

  // Keep ref in sync so interval closure always reads fresh state
  useEffect(() => {
    deleteProgressRef.current = deleteProgressByCampaign;
  }, [deleteProgressByCampaign]);

  useEffect(() => {
    if (activeTab !== 'trash') {
      sweepDoneRef.current = false;
      return;
    }
    if (!isAdmin) return;
    if (!trashCampaignIds) return;

    let stopped = false;

    const pollIds = async (idsToCheck: string[]) => {
      if (!idsToCheck.length) return;

      const entries = await Promise.all(
        idsToCheck.map(async id => {
          try {
            const s = await campaignService.getPermanentDeleteStatus(id);
            if (!s) return [id, null] as const;
            return [
              id,
              {
                status: s.status,
                progressPercent: s.progressPercent,
                deletedLeads: s.deletedLeads,
                totalLeads: s.totalLeads,
                errorMessage: s.errorMessage,
              },
            ] as const;
          } catch {
            return [id, null] as const;
          }
        })
      );

      if (stopped) return;

      const next: Record<string, any> = {};
      for (const [id, val] of entries) {
        if (val) next[id] = val;
      }

      let hadCompleted = false;
      setDeleteProgressByCampaign(prev => {
        hadCompleted = idsToCheck.some(
          id => (prev[id]?.status === 'queued' || prev[id]?.status === 'running') && !next[id]
        ) || entries.some(([, val]) => val?.status === 'completed');
        return { ...prev, ...next };
      });

      if (hadCompleted) {
        await fetchCampaigns();
        await fetchCampaignCounts();
      }
    };

    const allTrashIds = trashCampaignIds.split(',').filter(Boolean);
    void pollIds(allTrashIds).then(() => { sweepDoneRef.current = true; });

    const timer = setInterval(() => {
      const activeIds = Object.entries(deleteProgressRef.current)
        .filter(([, v]) => v?.status === 'queued' || v?.status === 'running')
        .map(([id]) => id);
      void pollIds(activeIds);
    }, 7000);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [activeTab, trashCampaignIds, isAdmin, fetchCampaigns]);

  const fetchCampaignCounts = async () => {
    try {
      let counts;
      if (isAdmin) {
        counts = await campaignService.getCampaignCountsForAdmin();
      } else {
        counts = await campaignService.getCampaignCounts();
      }
      // Normalize counts to ensure "all" is present
      const normalized = {
        ...counts,
        all: (counts.total || 0) - (counts.trash || 0),
      };
      setCampaignCounts(normalized);
    } catch (error) {
      console.error('Error fetching campaign counts:', error);
      // Don't show toast error for counts as it's not critical
    }
  };

  const handleCampaignAction = async (campaignId: string, action: string) => {
    try {
      setActionLoading(campaignId);
      
      switch (action) {
        case 'start':
        case 'resume':
          await campaignService.startCampaign(campaignId);
          toast.success('Campaign turned on');
          break;
        case 'pause':
          await campaignService.pauseCampaign(campaignId);
          toast.success('Campaign turned off');
          break;
        case 'complete':
          await campaignService.completeCampaign(campaignId);
          toast.success('Campaign completed successfully');
          break;
        case 'restart':
          await campaignService.restartCampaign(campaignId);
          toast.success('Campaign restarted successfully');
          break;
        case 'delete':
          await campaignService.deleteCampaign(campaignId);
          toast.success('Campaign moved to trash');
          break;
      }
      
      await fetchCampaigns();
      await fetchCampaignCounts();
    } catch (error: any) {
      console.error('Error updating campaign:', error);
      
      // Extract specific error message from backend response
        let errorMessage = 'Failed to update campaign';
        // Backend sends: { error: { message: "...", code: "...", statusCode: ... } }
        if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicateCampaign = async (campaignId: string) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    const defaultName = campaign ? `${campaign.name} (Copy)` : 'Campaign Copy';
    
    const name = prompt('Enter a name for the duplicated campaign:', defaultName);
    if (!name || name.trim() === '') {
      return; // User cancelled or entered empty name
    }
    
    try {
      setActionLoading(campaignId);
      const duplicatedCampaign = await campaignService.duplicateCampaign(campaignId, name.trim());
      toast.success('Campaign duplicated successfully');
      await fetchCampaigns();
      navigate(`/campaigns/${duplicatedCampaign.id}/edit`);
    } catch (error: any) {
      console.error('Error duplicating campaign:', error);
      
      let errorMessage = 'Failed to duplicate campaign';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreCampaign = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await campaignService.restoreCampaign(campaignId);
      toast.success('Campaign restored successfully');
      await fetchCampaigns();
      await fetchCampaignCounts();
    } catch (error) {
      console.error('Error restoring campaign:', error);
      toast.error('Failed to restore campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = (campaignId: string, campaignName: string) => {
    setCampaignToDelete({ id: campaignId, name: campaignName });
    setShowDeleteDialog(true);
  };

  const confirmPermanentDelete = async () => {
    if (!campaignToDelete) return;
    
    try {
      setActionLoading(campaignToDelete.id);
      await campaignService.permanentlyDeleteCampaign(campaignToDelete.id);
      toast.success('Permanent delete queued. Progress will appear on this card.');
      // Seed a placeholder so the interval immediately starts tracking this campaign
      setDeleteProgressByCampaign(prev => ({
        ...prev,
        [campaignToDelete.id]: { status: 'queued', progressPercent: 0, deletedLeads: 0, totalLeads: 0, errorMessage: null },
      }));
      await fetchCampaigns();
      await fetchCampaignCounts();
    } catch (error) {
      console.error('Error permanently deleting campaign:', error);
      toast.error('Failed to permanently delete campaign');
    } finally {
      setActionLoading(null);
      setShowDeleteDialog(false);
      setCampaignToDelete(null);
    }
  };



  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <Card className="p-6 mb-8">
          <CardContent className="p-0">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold mb-2 text-foreground">Campaigns</h1>
                <p className="text-muted-foreground">Manage your AI calling campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 mb-8">
        <CardContent className="p-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold mb-2 text-foreground">Campaigns</h1>
              <p className="text-muted-foreground">Manage your AI calling campaigns</p>
            </div>
            {isAdmin && (
              <Button variant="primary" size="sm" to="/campaigns/create">
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search campaigns by name..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="input pl-10 w-full"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setCurrentPage(1);
                setActiveTab(tab.key);
              }}
              className={clsx(
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors'
              )}
            >
              {tab.label}
              <span
                className={clsx(
                  activeTab === tab.key
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground',
                  'ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium'
                )}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {(() => {
        const totalPages = Math.max(1, Math.ceil(totalCampaigns / campaignsPerPage));
        return campaigns.length === 0 && !loading ? (
          <div className="text-center py-12">
            <PhoneIcon className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium text-foreground">{searchQuery ? 'No matching campaigns' : 'No campaigns'}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQuery ? 'Try a different search term.' : 'Use the "Create Campaign" button above to get started.'}
            </p>
          </div>
        ) : (
          <>
          <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 stagger-fade-in">
            {campaigns.map((campaign) => {
              const deleteProgress = deleteProgressByCampaign[campaign.id];
              const isDeleteInProgress =
                campaign.status === 'trash' &&
                (deleteProgress?.status === 'queued' || deleteProgress?.status === 'running');
              return (
            <div 
              key={campaign.id} 
              className="card card-interactive p-4 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center flex-wrap gap-2">
                  <StatusBadge status={campaign.status} type="campaign" variant="default" />
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    campaign.campaignType === 'bot'
                      ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'
                      : campaign.campaignType === 'text'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  }`}>
                    {campaign.campaignType === 'bot'
                      ? 'AI Calling'
                      : campaign.campaignType === 'text'
                        ? 'SMS'
                        : 'Human'}
                  </span>
                </div>
                <Menu as="div" className="relative">
                  <Menu.Button 
                    className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </Menu.Button>
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-card border border-border shadow-lg ring-1 ring-black/5 focus:outline-none">
                      <div className="py-1 px-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Button
                              to={`/campaigns/${campaign.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className={clsx(
                                active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                'block px-4 py-2 text-sm rounded-lg w-full text-left'
                              )}
                              variant="ghost"
                            >
                              View Details
                            </Button>
                          )}
                        </Menu.Item>
                        {isAdmin && (
                          <Menu.Item>
                            {({ active }) => (
                              <Button
                                to={`/campaigns/${campaign.id}/edit`}
                                onClick={(e) => e.stopPropagation()}
                                className={clsx(
                                  active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                  'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                )}
                                variant="ghost"
                              >
                                Edit Campaign
                              </Button>
                            )}
                          </Menu.Item>
                        )}
                        {isAdmin && (campaign.campaignType === 'human' || campaign.campaignType === 'text') && (
                          <Menu.Item>
                            {({ active }) => (
                              <Button
                                to={`/campaigns/${campaign.id}/edit?section=assign-agents`}
                                onClick={(e) => e.stopPropagation()}
                                className={clsx(
                                  active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                  'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                )}
                                variant="ghost"
                              >
                                <UserGroupIcon className="h-4 w-4 mr-2 inline" />
                                Assign Agents
                              </Button>
                            )}
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          {({ active }) => (
                            <Button
                              to={`/campaigns/${campaign.id}/leads`}
                              onClick={(e) => e.stopPropagation()}
                              className={clsx(
                                active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                'block px-4 py-2 text-sm rounded-lg w-full text-left'
                              )}
                              variant="ghost"
                            >
                              Manage Leads
                            </Button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Button
                              to={`/campaigns/${campaign.id}/results`}
                              onClick={(e) => e.stopPropagation()}
                              className={clsx(
                                active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                'block px-4 py-2 text-sm rounded-lg w-full text-left'
                              )}
                              variant="ghost"
                            >
                              View Results
                            </Button>
                          )}
                        </Menu.Item>
                        {isAdmin && (
                          <Menu.Item>
                             {({ active }) => (
                              <Button
                                onClick={(e) => {
                                   e.stopPropagation();
                                   handleDuplicateCampaign(campaign.id);
                                 }}
                                className={clsx(
                                  active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                  'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                )}
                                variant="ghost"
                              >
                                <DocumentDuplicateIcon className="h-4 w-4 mr-2 inline" />
                                Duplicate Campaign
                              </Button>
                             )}
                           </Menu.Item>
                        )}
                         
                         {/* Trash-specific actions */}
                         {isAdmin && campaign.status === 'trash' && (
                           <>
                             <div className="border-t border-border my-1" />
                             <Menu.Item>
                               {({ active }) => (
                                 <Button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleRestoreCampaign(campaign.id);
                                   }}
                                   disabled={actionLoading === campaign.id || isDeleteInProgress}
                                   className={clsx(
                                     active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                     'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                   )}
                                   variant="ghost"
                                 >
                                   <ArrowUturnLeftIcon className="h-4 w-4 mr-2 inline" />
                                   Restore Campaign
                                 </Button>
                               )}
                             </Menu.Item>
                             <Menu.Item>
                               {({ active }) => (
                                 <Button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handlePermanentDelete(campaign.id, campaign.name);
                                   }}
                                   disabled={actionLoading === campaign.id}
                                   className={clsx(
                                     active ? 'bg-red-50 text-red-600' : 'text-red-600',
                                     'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                   )}
                                   variant="ghost"
                                 >
                                   <TrashIcon className="h-4 w-4 mr-2 inline" />
                                   Delete Permanently
                                 </Button>
                               )}
                             </Menu.Item>
                           </>
                         )}
                         
                         {/* Move to trash for non-trash campaigns */}
                         {campaign.status !== 'trash' && (
                           <>
                             <div className="border-t border-border my-1" />
                             <Menu.Item>
                               {({ active }) => (
                                 <Button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleCampaignAction(campaign.id, 'delete');
                                   }}
                                   disabled={actionLoading === campaign.id}
                                   className={clsx(
                                     active ? 'bg-red-50 text-red-600' : 'text-red-600',
                                     'block px-4 py-2 text-sm rounded-lg w-full text-left'
                                   )}
                                   variant="ghost"
                                 >
                                   <TrashIcon className="h-4 w-4 mr-2 inline" />
                                   Move to Trash
                                 </Button>
                               )}
                             </Menu.Item>
                           </>
                         )}
                      </div>
                    </Menu.Items>
                </Menu>
              </div>
              
              <div className="mb-4">
                <Button
                  to={`/campaigns/${campaign.id}`}
                  className="text-lg font-semibold text-foreground hover:text-primary transition-colors line-clamp-1 p-0 h-auto justify-start"
                  variant="ghost"
                >
                  {campaign.name}
                </Button>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {campaign.description || 'No description'}
                </p>
                {isAdmin && (campaign as any).userEmail && (
                  <p className="text-xs text-blue-600 mt-2 font-medium">
                    Owner: {(campaign as any).userEmail}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-2 sm:p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-semibold text-foreground">{campaign.stats?.totalLeads || 0}</div>
                  <div className="text-xs text-muted-foreground">Total Leads</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-semibold text-foreground">{campaign.stats?.remainingLeads || 0}</div>
                  <div className="text-xs text-muted-foreground">Remaining Leads</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-semibold text-foreground">{campaign.stats?.successfulCalls || 0}</div>
                  <div className="text-xs text-muted-foreground">Calls Made</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-semibold text-foreground">{campaign.stats?.activeCalls || 0}</div>
                  <div className="text-xs text-muted-foreground">Active Calls</div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  {campaign.stats?.interestedLeads || 0} interested
                  {campaign.stats && campaign.stats.totalCalls > 0 && (
                    <span className="ml-2 text-primary font-medium">
                      ({((campaign.stats.interestedLeads / campaign.stats.totalCalls) * 100).toFixed(1)}%)
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Regular campaign actions - hidden for trash campaigns */}
                  {campaign.status !== 'trash' && (
                    <>
                      {campaign.status === 'draft' && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleCampaignAction(campaign.id, 'start'); }}
                          disabled={actionLoading === campaign.id}
                          variant="primary"
                          size="sm"
                          loading={actionLoading === campaign.id}
                          className="flex-shrink-0"
                        >
                          <PlayIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          Turn On
                        </Button>
                      )}
                      {campaign.status === 'active' && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleCampaignAction(campaign.id, 'pause'); }}
                          disabled={actionLoading === campaign.id}
                          variant="secondary"
                          size="sm"
                          loading={actionLoading === campaign.id}
                          className="flex-shrink-0"
                        >
                          <PauseIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          Turn Off
                        </Button>
                      )}
                      {campaign.status === 'paused' && (
                        <>
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleCampaignAction(campaign.id, 'resume'); }}
                            disabled={actionLoading === campaign.id}
                            variant="primary"
                            size="sm"
                            loading={actionLoading === campaign.id}
                            className="flex-shrink-0"
                          >
                            <PlayIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                            Turn On
                          </Button>
                          {isAdmin && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleCampaignAction(campaign.id, 'complete'); }}
                              disabled={actionLoading === campaign.id}
                              variant="secondary"
                              size="sm"
                              loading={actionLoading === campaign.id}
                              className="flex-shrink-0"
                            >
                              <StopIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                              Complete
                            </Button>
                          )}
                        </>
                      )}
                      {isAdmin && campaign.status === 'completed' && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleCampaignAction(campaign.id, 'restart'); }}
                          disabled={actionLoading === campaign.id}
                          variant="primary"
                          size="sm"
                          loading={actionLoading === campaign.id}
                          className="flex-shrink-0"
                        >
                          <ArrowPathIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          Restart
                        </Button>
                      )}
                    </>
                  )}
                  
                  {/* Trash campaign actions */}
                  {isAdmin && campaign.status === 'trash' && (
                    <>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleRestoreCampaign(campaign.id); }}
                        disabled={actionLoading === campaign.id || isDeleteInProgress}
                        variant="primary"
                        size="sm"
                        loading={actionLoading === campaign.id}
                        className="flex-shrink-0"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                        Restore
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handlePermanentDelete(campaign.id, campaign.name); }}
                        disabled={actionLoading === campaign.id || isDeleteInProgress}
                        variant="destructive"
                        size="sm"
                        loading={actionLoading === campaign.id}
                        className="flex-shrink-0"
                      >
                        <TrashIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                        Delete Permanently
                      </Button>
                    </>
                  )}
                </div>
                {campaign.status === 'trash' && deleteProgress && (
                  <div className="mt-3 p-3 rounded-lg border border-border bg-muted/20">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-foreground">
                        Permanent delete: {deleteProgress.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {deleteProgress.progressPercent}%
                      </span>
                    </div>
                    <div className="w-full h-2 rounded bg-muted overflow-hidden">
                      <div
                        className={clsx(
                          'h-2 transition-all duration-300',
                          deleteProgress.status === 'failed' ? 'bg-red-500' : 'bg-primary'
                        )}
                        style={{ width: `${Math.max(0, Math.min(100, deleteProgress.progressPercent))}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Leads deleted: {deleteProgress.deletedLeads.toLocaleString()} / {deleteProgress.totalLeads.toLocaleString()}
                    </p>
                    {deleteProgress.status === 'failed' && deleteProgress.errorMessage && (
                      <p className="text-xs text-red-600 mt-1">Error: {deleteProgress.errorMessage}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {totalCampaigns === 0 ? 0 : (currentPage - 1) * campaignsPerPage + 1}–{Math.min(currentPage * campaignsPerPage, totalCampaigns)} of {totalCampaigns} campaigns
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce<(number | string)[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    typeof p === 'string' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === p
                            ? 'bg-primary text-primary-foreground'
                            : 'border border-border hover:bg-muted text-foreground'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          </>
        );
      })()}

      {/* Permanent Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setCampaignToDelete(null);
        }}
        onConfirm={confirmPermanentDelete}
        title="Permanently Delete Campaign"
        message={
          campaignToDelete
            ? `Are you sure you want to permanently delete "${campaignToDelete.name}"? This action cannot be undone and all campaign data, including call history and leads, will be lost forever.`
            : ''
        }
        confirmText="Delete Permanently"
        cancelText="Cancel"
        isLoading={actionLoading === campaignToDelete?.id}
        variant="destructive"
      />
    </div>
  );
};

export default CampaignsPage;